import { CSSProperties, useState } from 'react';

import { nextMonday, startOfMonth } from 'date-fns';
import { useYearQuarterNavigation } from 'hooks';
import { useRecoilState, useRecoilValue } from 'recoil';

import {
  AdditionalCapacityWithoutId,
  FunctionCapacityBalance,
  Scenario
} from 'components/types';

import { ScenarioAdditionalCapacityType, Units } from 'components/enums';

import { getStartDateFromWeekNumber, toLocalISOString } from 'utils/date';

import { scenarioAtom } from '../../../../atoms';
import { updateFlexForecastAtom } from '../../../atoms';

import { Box, styled, TableCell } from '@mui/material';

import {
  NumericTableCell,
  ZebraStripedTableRow
} from 'components/shared/Table';

import AddEditAdditionalCapacityDialog from '../../../components/AddEditAdditionalCapacityDialog';

const UnderlinedNumericTableCell = styled(NumericTableCell)`
  text-decoration: underline;
  cursor: pointer;
`;

type Props = {
  functionCapacityBalance: FunctionCapacityBalance;
  style?: CSSProperties;
};

type AdditionalCapacityWithoutFunction = Omit<
  AdditionalCapacityWithoutId,
  'function'
>;

const DetailRow = ({ functionCapacityBalance, style }: Props) => {
  const [
    shouldShowAddAdditionalCapacityDialog,
    setShouldShowAddAdditionalCapacityDialog
  ] = useState<boolean>(false);
  const [dataForAdditionalCapacity, setDataForAdditionalCapacity] = useState<
    AdditionalCapacityWithoutId | AdditionalCapacityWithoutFunction | null
  >(null);
  const { isViewPerWeek } = useYearQuarterNavigation();
  const scenario = useRecoilValue(scenarioAtom) as Scenario;
  const [, setUpdateFlexForecast] = useRecoilState(updateFlexForecastAtom);

  const onClickCell = (period) => {
    const { hours, year, number } = period;

    const startDate = isViewPerWeek
      ? getStartDateFromWeekNumber(number, year)
      : startOfMonth(new Date(year, number - 1));
    const endDate = isViewPerWeek
      ? nextMonday(startDate)
      : startOfMonth(new Date(year, number));

    const baseData: AdditionalCapacityWithoutFunction = {
      startDate: toLocalISOString(startDate),
      endDate: toLocalISOString(endDate),
      type:
        hours > 0
          ? ScenarioAdditionalCapacityType.LOAN
          : ScenarioAdditionalCapacityType.FLEX,
      unit: isViewPerWeek ? Units.WEEK : Units.MONTH,
      hours: Math.abs(hours),
      quantity: 1,
      scenarioId: scenario.scenarioId,
      description: '',
      organizationalUnit: null
    };

    if (functionCapacityBalance.function) {
      const data: AdditionalCapacityWithoutId = {
        ...baseData,
        function: functionCapacityBalance.function
      };
      setDataForAdditionalCapacity(data);
    } else {
      setDataForAdditionalCapacity(baseData);
    }

    setShouldShowAddAdditionalCapacityDialog(true);
  };

  const handleCloseAdditionalCapacity = () => {
    setDataForAdditionalCapacity(null);
    setShouldShowAddAdditionalCapacityDialog(false);
  };

  const onSaveAdditionalCapacity = () => {
    handleCloseAdditionalCapacity();
    setUpdateFlexForecast(true);
  };

  return (
    <>
      <ZebraStripedTableRow style={style}>
        <>
          <TableCell width={200} sx={{ position: 'relative' }}>
            <Box display="flex" pl="44px">
              <Box
                sx={{
                  backgroundColor:
                    functionCapacityBalance.function?.colorCode ?? null,
                  position: 'absolute',
                  left: 32,
                  top: 0,
                  bottom: 0,
                  width: '8px',
                  padding: 0
                }}
              />
              <Box>{functionCapacityBalance.function?.description ?? ''}</Box>
            </Box>
          </TableCell>
        </>
        {functionCapacityBalance.periodHourBalances.map((period) =>
          !scenario.readOnly ? (
            <UnderlinedNumericTableCell
              key={`${functionCapacityBalance.uuid}_period_${period.number}`}
              onClick={() => onClickCell(period)}
            >
              {period.hours ? Math.round(period.hours) : ''}
            </UnderlinedNumericTableCell>
          ) : (
            <NumericTableCell
              key={`${functionCapacityBalance.uuid}_period_${period.number}`}
            >
              {period.hours ? Math.round(period.hours) : ''}
            </NumericTableCell>
          )
        )}
        <NumericTableCell width={68}>
          {functionCapacityBalance.totalYearHoursBalance !== 0
            ? Math.round(functionCapacityBalance.totalYearHoursBalance)
            : ''}
        </NumericTableCell>
      </ZebraStripedTableRow>
      {shouldShowAddAdditionalCapacityDialog && dataForAdditionalCapacity && (
        <AddEditAdditionalCapacityDialog
          isOpen={shouldShowAddAdditionalCapacityDialog}
          handleClose={handleCloseAdditionalCapacity}
          onSave={onSaveAdditionalCapacity}
          capacity={dataForAdditionalCapacity}
        />
      )}
    </>
  );
};

export default DetailRow;
